/* ==========================================================================
   occasion-dated-ship-timing.js
   ONE script for every ship-timing block (Addendum F11). It branches on the
   root's data-ship-timing-mode:

     week      product.christmas. Two cards; card 2 offers December delivery
               WEEKS from GET /eta/ranges. Spec Sections 3.2 to 3.6.
     date      product.evergreen, product.baby, product.sympathy. Two cards;
               card 2 is a native date field, "deliver by a date". Addendum E2.
     birthday  product.birthday. Properties only: no postal field, no engine
               call, no add-to-cart guard. Addendum E1 plus Addendum F4.

   SUPERSEDES assets/occasion-dated-christmas-ship-timing.js, which is left on
   staging as an unloaded orphan under --nodelete and is on no live theme.

   Division of work with assets/occasion-dated-product-page.js: initReserve()
   there still owns radio -> properties[_Ship Preference], and in birthday mode
   it also owns the date field -> properties[Do Not Open Until]. This file never
   writes either of those two in birthday mode. When this file changes a radio
   programmatically it dispatches `change` so initReserve() stays in step.

   The engine base URL is read from the snippet's data-eta-endpoint (block
   setting xm_eta_endpoint / xd_eta_endpoint), never typed here. Ranges URL =
   that + "/ranges". Every calendar label is computed from ISO dates; no
   weekday and no month name is ever typed by a merchant.

   CARD 2 STEPS (Findings 1 and 2, second pass, ruled by Tommy 2026-09-22).
   Only the selected card is open (CSS). Card 2 shows one step at a time and
   has its OWN postal field; card 1's field stays in card 1. Both call
   lookup(), both write the one `postal`, and a success fills both fields.
   Card 2 asks for a postal code only when the week starts, or the date
   falls, within the block's lead days (xm_postal_lead_days /
   xd_postal_lead_days, default 21). Errors show only on Add to Cart or an
   engine rejection, never as a step instruction, in one red box style that
   card 1's invalid-code line now shares.
   ITEM 76, DATE MODE ONLY, ruled by Tommy in full 2026-10-02, built
   2026-10-06, SUPERSEDING the lead-days sentence above for the DATE card
   (the sentence is kept as the record; the week card keeps its gate). The
   date card asks for the postal code BEFORE the date, always, unless one is
   already known from card 1 or the session (F8): nearChoice() is true in
   date mode, so xd_postal_lead_days is no longer read there. paintSteps()
   shows the ask above the date field, DISABLES the date input and shows the
   helper line [data-xd-date-help] while no code is known, and shows the
   "Postal code: ... Change" summary with the date enabled once one is. The
   probe (C2) fires when card 2 is selected rather than on a near date.
   blockAtc() names need-postal before need-date in date mode, because the
   date is disabled while the postal step is on screen. Fail-open (C) stands:
   engine unreachable means no ask and an enabled date. The original defect,
   a calendar opened on the weekday-only floor before a postal code is known,
   has no remaining state: the calendar cannot be opened until the engine's
   floor is in.

   ENGINE UNREACHABLE MEANS FAIL-OPEN (ruled 2026-09-22). `engineDown` goes
   true, for the page, when a lookup gets no data, the one-off probe fails,
   or the load-time ranges call fails. Card 2 then asks for no postal code,
   shows the week or date with the calm line, and the guard never asks for
   one. A failed ranges call falls back to the block's xm_fallback_weeks.

   E1 VOCABULARY, properties[Shipping needed], UPDATED 2026-09-23 (ruled by
   Tommy): it now carries the checkout's method name WITHOUT the symbol, the
   same string the tight sentence prints, from serviceName(): "Canada Post
   Xpresspost", "Canada Post Priority", "FedEx Express Saver", "FedEx Priority
   Overnight" or "FedEx First Overnight". Before, it carried the bare Canada
   Post name ("Xpresspost", "Priority") and, from 2026-09-22, a FedEx row title
   as the shadow cart returned it, symbol included.
   RENAMED 2026-09-25: the property is now properties[Arrives by your date only if you select]; value unchanged; the JS finds the input by [data-mdp-shipping-needed-input], so no code here changes.

   ITEM 86, THE DATE CARD SPEAKS IN THE CUSTOMER'S DATE (ruled by Tommy
   2026-09-27). Card 2's cards and Compare link show only on a TIGHT verdict,
   date and week alike. Date card: (1) comfortable prints "Estimated arrival
   by <chosen date>", the ship-a-day-early line and a case-1 "Need it
   sooner?" line, hiding the always-on line; (2) tight prints the named
   service's carrier date, time and live price; (3) past keeps the block and
   prints "Earliest available delivery is ...". The week card's copy is
   unchanged. No engine call and no property changed.

   ITEM 87, THE DATE CARD SKIPS THE SHADOW-CART PRICE REQUEST ON A
   COMFORTABLE VERDICT (ruled by Tommy 2026-09-27). paintPrices() no longer
   fires the Storefront cartCreate for card 2 while mode is date and the
   settled verdict is comfortable, since item 86 already hides its cards and
   Compare link there. Card 1, tight, past and the Christmas week card are
   unaffected.

   ATC guard: occasion-dated-multi-add-fix.js intercepts the ATC click with a
   DOCUMENT capture listener and can bypass the native submit. This guard
   listens on WINDOW in the capture phase, which runs first, so an incomplete
   card 2 order is stopped before either path runs. It writes nothing inside
   #gsAppContainer. Birthday mode installs no guard at all (Addendum F4).
   ========================================================================== */
(function () {
  'use strict';

  /* Shared across the christmas, date and birthday blocks, good for the
     current Toronto day only (Addendum F8). Replaces glitter:xmas-postal. */
  var STORE_KEY = 'glitter:ship-postal';
  var LEGACY_STORE_KEY = 'glitter:xmas-postal';
  /* ESTIMATOR LOADING AND FAILURE STATE (card 1, 2026-10-08): a postal
     lookup is abandoned after LOOKUP_MS and treated as the engine being
     unreachable; the busy look (spinner, "Checking" label, read-only
     field) waits BUSY_DELAY_MS so a fast answer never flashes it. */
  var LOOKUP_MS = 12000;
  var BUSY_DELAY_MS = 150;

  var MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  var MONTHS_LONG = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  var WEEKDAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

  /* Canada Post service codes the engine quotes, in the order Addendum F6
     requires: free ground first, then the express options. */
  var SERVICE_ORDER = ['DOM.EP', 'DOM.XP', 'DOM.PC'];
  /* TOMBSTONE, F17, ruled by Tommy 2026-09-20. A SERVICE_TITLES map used to sit
     here and gave codeOf() a title fallback:
       'DOM.EP': ['expedited parcel', 'expedited'],
       'DOM.XP': ['xpresspost'],
       'DOM.PC': ['priority']
     It was DELETED ON PURPOSE. 'priority' matched "FedEx Priority Overnight",
     which then wore a Canada Post arrival date the engine never computed for it.
     Classification is BY CODE ONLY now. Do not put this map back. */

  /* ---------- dates ---------- */
  function parseIso(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
    if (!m) return null;
    var y = +m[1], mo = +m[2], d = +m[3];
    var dt = new Date(Date.UTC(y, mo - 1, d));
    if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null;
    return dt;
  }
  function isoOf(dt) {
    return dt.getUTCFullYear() + '-' +
      ('0' + (dt.getUTCMonth() + 1)).slice(-2) + '-' +
      ('0' + dt.getUTCDate()).slice(-2);
  }
  function addDays(iso, n) {
    var d = parseIso(iso);
    if (!d) return '';
    d.setUTCDate(d.getUTCDate() + n);
    return isoOf(d);
  }
  /* Today in America/Toronto, so a shopper in Vancouver and the warehouse
     agree on what "today" means. Falls back to the device date. */
  function todayToronto() {
    try {
      var s = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Toronto', year: 'numeric', month: '2-digit', day: '2-digit'
      }).format(new Date());
      if (parseIso(s)) return s;
    } catch (e) {}
    var n = new Date();
    return isoOf(new Date(Date.UTC(n.getFullYear(), n.getMonth(), n.getDate())));
  }
  /* "Thursday, December 3" */
  function weekdayLong(iso) {
    var d = parseIso(iso);
    if (!d) return '';
    return WEEKDAYS[d.getUTCDay()] + ', ' + MONTHS_LONG[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }
  /* "Oct 1, 2026" */
  function shortWithYear(iso) {
    var d = parseIso(iso);
    if (!d) return '';
    return MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate() + ', ' + d.getUTCFullYear();
  }
  /* "Oct 1" */
  function shortNoYear(iso) {
    var d = parseIso(iso);
    if (!d) return '';
    return MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }
  /* "Tuesday, Sep 22", for FedEx rows (Addendum G6). Weekday plus short date,
     so the row carries the day name without running as long as weekdayLong. */
  function weekdayShort(iso) {
    var d = parseIso(iso);
    if (!d) return '';
    return WEEKDAYS[d.getUTCDay()] + ', ' + MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }
  /* "Tue, Sep 29", for the date card's tight sentence and its too-early
     message (item 86, ruled by Tommy 2026-09-27). */
  function dayShort(iso) {
    var d = parseIso(iso);
    if (!d) return '';
    return WEEKDAYS[d.getUTCDay()].slice(0, 3) + ', ' + MONTHS[d.getUTCMonth()] + ' ' + d.getUTCDate();
  }
  /* "HH:MM" from the engine to 12-hour form: 10:00 -> "10:00 AM",
     13:30 -> "1:30 PM", 23:59 -> "11:59 PM". Anything unparseable yields ''
     and the caller simply omits the time. */
  function time12(hhmm) {
    var m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || '').trim());
    if (!m) return '';
    var h = parseInt(m[1], 10);
    if (isNaN(h) || h < 0 || h > 23) return '';
    var suffix = h < 12 ? 'AM' : 'PM';
    var h12 = h % 12;
    if (h12 === 0) h12 = 12;
    return h12 + ':' + m[2] + ' ' + suffix;
  }
  /* What a FedEx row actually prints after the word "by". Ruled by Tommy
     2026-09-20: 23:59 reads "end of day", not "11:59 PM". It is the carrier's
     end-of-day marker for Ground rather than a real commitment minute, and
     "by 11:59 PM" sat oddly beside a genuine "by 10:00 AM" on the same list.
     Every other time keeps its 12-hour form. Both blocks share this file, so
     both get it. */
  function arrivalByLabel(hhmm) {
    var t = time12(hhmm);
    if (!t) return '';
    return /^(23:59|11:59 PM)$/.test(t) ? 'end of day' : t;
  }
  /* "Dec 7 to 11", or "Nov 30 to Dec 4" across a month boundary */
  function rangeLabel(startIso, endIso) {
    var a = parseIso(startIso), b = parseIso(endIso);
    if (!a || !b) return '';
    var left = MONTHS[a.getUTCMonth()] + ' ' + a.getUTCDate();
    var right = (a.getUTCMonth() === b.getUTCMonth() ? '' : MONTHS[b.getUTCMonth()] + ' ') + b.getUTCDate();
    return left + ' to ' + right;
  }
  function rangeLabelWithYear(startIso, endIso) {
    var b = parseIso(endIso);
    return rangeLabel(startIso, endIso) + (b ? ', ' + b.getUTCFullYear() : '');
  }

  /* ---------- postal ---------- */
  function normPostal(v) { return (v || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); }
  function displayPostal(n) { return n.length === 6 ? n.slice(0, 3) + ' ' + n.slice(3) : n; }

  /* Province from the postal code's first letter, for the shadow cart's
     delivery address. Country is always CA. X covers NT and NU; NT is used
     because the shadow cart needs one value and the rate is the same. */
  var FSA_PROVINCE = {
    A: 'NL', B: 'NS', C: 'PE', E: 'NB', G: 'QC', H: 'QC', J: 'QC',
    K: 'ON', L: 'ON', M: 'ON', N: 'ON', P: 'ON', R: 'MB', S: 'SK',
    T: 'AB', V: 'BC', X: 'NT', Y: 'YT'
  };
  function provinceOf(n) { return FSA_PROVINCE[(n || '').charAt(0)] || ''; }

  /* F8: the stored value carries the Toronto date it was stored on. A stamp
     that is not today is ignored AND cleared, so a stale postal code can never
     pre-fill a field the next morning. */
  function storeGet() {
    try {
      var raw = window.sessionStorage.getItem(STORE_KEY);
      if (!raw) return '';
      var o = JSON.parse(raw);
      if (!o || !o.p) return '';
      if (o.d !== todayToronto()) { window.sessionStorage.removeItem(STORE_KEY); return ''; }
      return o.p;
    } catch (e) { return ''; }
  }
  function storeSet(v) {
    try { window.sessionStorage.setItem(STORE_KEY, JSON.stringify({ p: v, d: todayToronto() })); } catch (e) {}
  }
  /* The pre-F8 key carried a bare postal code with no stamp. Drop it once. */
  try { window.sessionStorage.removeItem(LEGACY_STORE_KEY); } catch (e) {}

  /* ---------- engine ---------- */
  var cache = Object.create(null); /* url -> Promise<json|null> for the page session */
  function getJson(url, signal) {
    if (!cache[url]) {
      cache[url] = fetch(url, { method: 'GET', mode: 'cors', credentials: 'omit', signal: signal || undefined })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return cache[url];
  }
  /* The ranges array of an ok /eta/ranges response, or null when the call
     failed or the shape is wrong. An empty array is a valid answer. */
  function validRanges(data) {
    if (!data || data.status !== 'ok' || !Array.isArray(data.ranges)) return null;
    var out = [];
    for (var i = 0; i < data.ranges.length; i++) {
      var r = data.ranges[i];
      if (r && parseIso(r.start) && parseIso(r.end)) out.push({ start: r.start, end: r.end, state: r.state || null });
    }
    return out;
  }
  /* { 'DOM.EP': '2026-10-01', ... } from an ok /eta response. */
  function arrivalsOf(data) {
    var out = Object.create(null);
    if (!data || data.status !== 'ok' || !Array.isArray(data.options)) return out;
    for (var i = 0; i < data.options.length; i++) {
      var o = data.options[i];
      if (o && o.service_code && parseIso(o.arrival)) out[o.service_code] = o.arrival;
    }
    return out;
  }
  /* Addendum G3: the engine's FedEx options, keyed by `intuitive_code`, which is
     the EXACT string the shadow cart returns as the option's `code`. The join is
     a strict equality lookup on that key and nothing else. NO TITLE MATCHING,
     per F17, which is also why the carrier's own service name is ignored here:
     the engine calls FEDEX_EXPRESS_SAVER "FedEx Economy" while the checkout
     calls it "FedEx Express Saver", and the page always shows the shadow cart's
     title (G4).

     Fail-open per G7: no `fedex` key, a non-ok status, or a missing arrival all
     leave the map empty, and an empty map means every FedEx row falls back to
     the "faster option" label exactly as before. A SHORT LIST IS NORMAL, NOT AN
     ERROR (G8): Iqaluit returns Ground alone because the express services do not
     serve it. */
  function fedexArrivalsOf(data) {
    var out = Object.create(null);
    var fx = data && data.fedex;
    if (!fx || fx.status !== 'ok' || !Array.isArray(fx.options)) return out;
    for (var i = 0; i < fx.options.length; i++) {
      var o = fx.options[i];
      if (o && o.intuitive_code && parseIso(o.arrival)) {
        out[o.intuitive_code] = { arrival: o.arrival, by: o.arrival_by || '' };
      }
    }
    return out;
  }
  /* F5: the earliest arrival across ALL quoted services, not free ground. A
     date only Xpresspost can reach must stay pickable. */
  function earliestArrival(arr) {
    var best = '';
    for (var k in arr) { if (!best || arr[k] < best) best = arr[k]; }
    return best;
  }
  /* The state of a target call. The engine names it `state`; older shapes put
     it in `status`. Anything unrecognised degrades to `estimate`, which shows
     the soft line and blocks nothing. */
  function targetState(data) {
    if (!data) return '';
    /* THE ENGINE PUTS THE STATE AT `order_by.state`, NOT AT THE TOP LEVEL.
       Reading `data.state` alone silently returned `estimate` for EVERY
       successful call, because `status` is `ok` and the last line below then
       fires. `data.state` is kept as first choice so an older or different
       shape still wins, and `order_by.state` is the one the engine actually
       sends today. Same story for the service name in tightService. */
    var ob = (data && data.order_by) || {};
    var s = data.state || ob.state || (data.status !== 'ok' ? data.status : '') || '';
    s = String(s).toLowerCase();
    if (s === 'comfortable' || s === 'tight' || s === 'past' || s === 'estimate') return s;
    if (s === 'unavailable' || s === 'invalid_postal' || s === 'invalid') return 'unavailable';
    /* The estimate fallback STAYS, because a genuinely unknown state should
       show the soft line and block nothing. What it must never do again is
       absorb a MISSING FIELD without a word: an `ok` answer that yields no
       recognisable state means the shape moved, so say so once. */
    if (data.status === 'ok') {
      if (!loggedStateFallback) {
        loggedStateFallback = true;
        console.error('[ship-timing] engine returned ok with no recognisable state; ' +
          'falling back to estimate. Checked data.state and data.order_by.state. Keys:',
          Object.keys(data), 'order_by keys:', Object.keys(ob));
      }
      return 'estimate';
    }
    return '';
  }
  var PRETTY = { 'DOM.EP': 'Expedited Parcel', 'DOM.XP': 'Xpresspost', 'DOM.PC': 'Priority' };
  /* The service the engine says can still make the date, for the `tight` line. */
  function tightService(data, arrivals, target) {
    /* `order_by.service` is where the engine names the service that still makes
       the date. The arrivals loop below recovers the right answer on its own,
       which is why this was invisible, but the engine's own answer beats an
       inferred one and is read first. */
    var ob = (data && data.order_by) || {};
    var name = data && (data.service_name || data.service_title || data.service || ob.service);
    if (name && typeof name === 'string' && !/^dom\./i.test(name)) return name;
    var code = name && String(name).toUpperCase();
    if (code && PRETTY[code]) return PRETTY[code];
    for (var i = 0; i < SERVICE_ORDER.length; i++) {
      var c = SERVICE_ORDER[i];
      if (c !== 'DOM.EP' && arrivals[c] && arrivals[c] <= target) return PRETTY[c];
    }
    return '';
  }

  /* ---------- shadow cart (Addendum E3, F12) ----------
     INERT until the block carries a non-blank Storefront token. It creates a
     Storefront cart holding only this variant with the E1 properties, sets the
     delivery address to the postal code and its province, and reads the
     delivery options. It never touches the shopper's real cart. Any failure at
     any step resolves to null and the block simply shows no prices. */
  var SF_VERSION = '2025-01';
  var priceCache = Object.create(null); /* variantId|postal|isoDate -> Promise */
  var loggedApiError = false; /* one console.error per page load, not per call */
  var loggedStateFallback = false; /* same, for an ok answer with no known state */

  /* A multipart/mixed @defer stream, or a plain JSON body, reduced to one
     merged payload. Incremental chunks are merged onto the initial data by
     walking each chunk's path, which is how the Storefront API delivers a
     deferred deliveryGroups field. */
  function readGraphql(res) {
    var ct = (res.headers.get('content-type') || '').toLowerCase();
    if (ct.indexOf('multipart/mixed') === -1) return res.json();
    var bm = /boundary="?([^";]+)"?/i.exec(ct);
    var boundary = bm ? bm[1] : '-';
    return res.text().then(function (body) {
      var parts = body.split('--' + boundary);
      var merged = null;
      for (var i = 0; i < parts.length; i++) {
        var raw = parts[i];
        var at = raw.indexOf('\r\n\r\n');
        var sep = 4;
        if (at === -1) { at = raw.indexOf('\n\n'); sep = 2; }
        if (at === -1) continue;
        var json = raw.slice(at + sep).replace(/\s*--\s*$/, '').trim();
        if (!json) continue;
        var chunk;
        try { chunk = JSON.parse(json); } catch (e) { continue; }
        if (!merged) { merged = chunk; continue; }
        var inc = chunk.incremental || (chunk.data ? [chunk] : []);
        for (var j = 0; j < inc.length; j++) {
          /* R5: a deferred chunk carries its OWN errors, separate from the
             top-level `errors`. Shopify throttles carrier-calculated rates and
             reports it here, as `data: null` plus a THROTTLED error, so a
             top-level check alone never sees it and the list hides in silence.
             Collect them so fetchPrices can log once. */
          if (inc[j] && inc[j].errors && inc[j].errors.length) {
            merged.deferErrors = (merged.deferErrors || []).concat(inc[j].errors);
          }
          mergeIncremental(merged, inc[j]);
        }
      }
      return merged || { data: null };
    });
  }
  function mergeIncremental(merged, piece) {
    if (!piece || !piece.data) return;
    var path = piece.path || [];
    var node = merged.data;
    for (var i = 0; i < path.length && node; i++) node = node[path[i]];
    if (!node) return;
    for (var k in piece.data) node[k] = piece.data[k];
  }

  var CART_MUTATION =
    'mutation ShadowCart($input: CartInput!) {' +
    '  cartCreate(input: $input) {' +
    '    cart {' +
    '      id' +
    '      ... @defer(label: "delivery") {' +
    /* withCarrierRates is what asks Shopify to call the carrier services, which
       is where Intuitive Shipping's rates come from; without it deliveryGroups
       comes back [] on a perfectly valid cart. Shopify REQUIRES it to sit inside
       @defer and refuses the query otherwise ("DeliveryGroups(withCarrierRates:
       true) must be called with @defer"), so the @defer above is load-bearing
       and must not be removed. */
    '        deliveryGroups(first: 3, withCarrierRates: true) { nodes { deliveryOptions {' +
    '          handle title code deliveryMethodType description' +
    '          estimatedCost { amount currencyCode }' +
    '        } } }' +
    '      }' +
    '    }' +
    '    userErrors { message }' +
    '  }' +
    '}';

  function fetchPrices(cfg) {
    var key = cfg.variantId + '|' + cfg.postal + '|' + (cfg.iso || '');
    if (priceCache[key]) return priceCache[key];
    var province = provinceOf(cfg.postal);
    var input = {
      lines: [{ merchandiseId: 'gid://shopify/ProductVariant/' + cfg.variantId, quantity: 1, attributes: cfg.attributes || [] }],
      buyerIdentity: {
        countryCode: 'CA',
        deliveryAddressPreferences: [{
          /* This slot is a MailingAddressInput, whose fields are `country` and
             `province`. It is NOT CartDeliveryAddressInput, which really does
             use countryCode and provinceCode; sending those names here is
             rejected outright. Short codes CA and ON are accepted. */
          deliveryAddress: { country: 'CA', province: province, zip: displayPostal(cfg.postal) }
        }]
      }
    };
    priceCache[key] = fetch('/api/' + SF_VERSION + '/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'multipart/mixed, application/json',
        'X-Shopify-Storefront-Access-Token': cfg.token
      },
      body: JSON.stringify({ query: CART_MUTATION, variables: { input: input } })
    })
      .then(function (r) { return r.ok ? readGraphql(r) : null; })
      .then(function (payload) {
        /* A GraphQL validation failure is HTTP 200 with `errors` and no `data`,
           so r.ok above is not enough on its own: the block would hide itself
           and leave nothing in the console to find. Log once, then fall through
           to the same null the rest of this chain returns. */
        if (payload && payload.errors && payload.errors.length) {
          if (!loggedApiError) {
            loggedApiError = true;
            console.error('[ship-timing] Storefront API returned errors:', payload.errors);
          }
          return null;
        }
        /* R5: deferred-chunk errors are logged but NOT treated as fatal, so a
           partial answer still renders whatever did arrive. The commonest one
           is THROTTLED, "Carrier-calculated rates through @defer is temporarily
           unavailable", which leaves deliveryGroups absent and falls through to
           the null return below on its own. */
        if (payload && payload.deferErrors && payload.deferErrors.length && !loggedApiError) {
          loggedApiError = true;
          console.error('[ship-timing] Storefront API deferred errors:', payload.deferErrors);
        }
        var cart = payload && payload.data && payload.data.cartCreate && payload.data.cartCreate.cart;
        var groups = cart && cart.deliveryGroups && cart.deliveryGroups.nodes;
        if (!groups || !groups.length) return null;
        var opts = groups[0].deliveryOptions || [];
        return opts.length ? opts : null;
      })
      .catch(function () { return null; });
    return priceCache[key];
  }

  /* Join a delivery option to a Canada Post service code BY CODE ONLY (F17,
     ruled by Tommy 2026-09-20). Anything whose code is not one of the three
     is treated as a non-Canada-Post carrier: it gets the "faster option"
     label and NO arrival date, because the engine quotes Canada Post only.

     THE TITLE FALLBACK IS RETIRED AND MUST NOT BE REINSTATED. It matched the
     bare substring 'priority', so "FedEx Priority Overnight" was classified as
     Canada Post Priority and printed a Canada Post arrival date that had been
     computed for a different carrier. F12's "then its title" wording is
     SUPERSEDED by F17. A missing date is harmless; a wrong date is the one
     thing this build must never show. */
  function codeOf(opt) {
    var raw = String(opt.code || '').toUpperCase();
    for (var i = 0; i < SERVICE_ORDER.length; i++) {
      if (raw.indexOf(SERVICE_ORDER[i]) !== -1) return SERVICE_ORDER[i];
    }
    return '';
  }
  /* F7, PROBE FIRST: the engine quotes Canada Post only, so a FedEx arrival can
     only come from a carrier estimate riding along in the option itself. Read
     one if it is there; show none if it is not. Nothing here quotes FedEx. */
  function carrierEstimate(opt) {
    var text = String(opt.description || '');
    var iso = /(\d{4}-\d{2}-\d{2})/.exec(text);
    if (iso && parseIso(iso[1])) return shortNoYear(iso[1]);
    var named = /\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*\.?\s+(\d{1,2})\b/.exec(text);
    if (named) return named[1] + ' ' + named[2];
    return '';
  }
  function money(opt) {
    var c = opt.estimatedCost;
    if (!c) return '';
    var amt = parseFloat(c.amount);
    if (isNaN(amt)) return '';
    if (amt === 0) return 'Free';
    return '$' + amt.toFixed(2);
  }
  /* The same number as a sortable float, for the price tie-break inside one
     arrival date. An unreadable price sorts last rather than as free, which is
     the safe direction: priceRow drops such a row anyway. */
  function priceAmount(opt) {
    var c = opt && opt.estimatedCost;
    var amt = c ? parseFloat(c.amount) : NaN;
    return isNaN(amt) ? Infinity : amt;
  }

  /* ---------- the block ---------- */
  function init(root) {
    if (root.__stInit) return;
    root.__stInit = true;

    var mode = root.getAttribute('data-ship-timing-mode') || 'week';
    var endpoint = root.getAttribute('data-eta-endpoint') || '';
    var rangesUrl = endpoint.replace(/\/+$/, '') + '/ranges';
    var xmas = root.getAttribute('data-xmas-date') || '';
    var token = (root.getAttribute('data-storefront-token') || '').trim();
    var fedexLogo = root.getAttribute('data-fedex-logo') || '';
    var cpLogo = root.getAttribute('data-canadapost-logo') || '';
    var copy = function (k) { return root.getAttribute('data-copy-' + k) || ''; };

    var form = root.closest('form');
    var radioAsap = root.querySelector('[data-mdp-ship-pref][value="asap"]');
    var radioAlt = root.querySelector('[data-mdp-ship-pref][value="reserve"]');

    /* card 1, shared by week and date mode */
    var asapBefore = root.querySelector('[data-xm-asap-before]');
    var arrival = root.querySelector('[data-xm-arrival]');
    var arrivalHead = root.querySelector('[data-xm-arrival-head]');
    var arrivalSub = root.querySelector('[data-xm-arrival-sub]');
    var openBtn = root.querySelector('[data-xm-open]');
    var postalForm = root.querySelector('[data-xm-postal-form]');
    var postalEl = root.querySelector('[data-mdp-postal]');
    var seeBtn = root.querySelector('[data-xm-see]');
    var downEl = root.querySelector('[data-xm-down]');
    var msgEl = root.querySelector('[data-xm-msg]');
    var changeBtn = root.querySelector('[data-xm-change]');

    /* week mode */
    var weekCard = root.querySelector('[data-xm-week-card]');
    var weeksEl = root.querySelector('[data-xm-weeks]');
    var weekResult = root.querySelector('[data-xm-week-result]');
    var weekHead = root.querySelector('[data-xm-week-head]');
    var weekSum = root.querySelector('[data-xm-week-sum]');
    var weekSumText = root.querySelector('[data-xm-week-sum-text]');
    var weekChange = root.querySelector('[data-xm-week-change]');
    var weekSub = root.querySelector('[data-xm-week-sub]');
    var stickerRow = root.querySelector('[data-xm-sticker-row]');
    var stickerCheck = root.querySelector('[data-mdp-sticker-check]');

    /* date mode */
    var dateCard = root.querySelector('[data-xd-date-card]');
    var dateEl = root.querySelector('[data-xd-date-input]');
    var dateResult = root.querySelector('[data-xd-result]');
    var dateHead = root.querySelector('[data-xd-head]');
    var dateSub = root.querySelector('[data-xd-sub]');
    /* Item 86: case 1's third line, and the always-on line it replaces. */
    var dateNote = root.querySelector('[data-xd-note]');
    var alwaysNote = root.querySelector('[data-xd-always-note]');
    /* Item 76: the line under the date field while it is disabled. */
    var dateHelp = root.querySelector('[data-xd-date-help]');

    /* card 2's own postal step and its errors, week and date mode alike.
       Nothing here is moved between cards: card 1 keeps its own field. */
    var cardErr = root.querySelector('[data-xm2-card-err]');
    var fieldErr = root.querySelector('[data-xm2-field-err]');
    var postal2Form = root.querySelector('[data-xm2-postal-form]');
    var postal2El = root.querySelector('[data-xm2-postal]');
    var confirm2Btn = root.querySelector('[data-xm2-confirm]');
    var postalSum = root.querySelector('[data-xm2-postal-sum]');
    var postalSumText = root.querySelector('[data-xm2-postal-sum-text]');
    var postalChange = root.querySelector('[data-xm2-postal-change]');
    /* Rule C/D: card 2 needs a postal code only this close to the week or date. */
    var leadDays = parseInt(root.getAttribute('data-postal-lead-days'), 10);
    if (isNaN(leadDays)) leadDays = 21;
    /* week mode: the calm line, shown only while the engine is unreachable */
    var confirmAfterEl = root.querySelector('[data-xm-confirm-after]');

    /* birthday mode reuses the field initReserve() already owns */
    if (mode === 'birthday') dateEl = root.querySelector('[data-mdp-dnod-visible]');

    /* hidden inputs, the E1 vocabulary */
    var inTiming = root.querySelector('[data-mdp-timing-input]');
    var inFrom = root.querySelector('[data-mdp-from-input]');
    var inBy = root.querySelector('[data-mdp-by-input]');
    var inDate = root.querySelector('[data-mdp-date-input]');
    var inPostal = root.querySelector('[data-mdp-postal-input]');
    var inNeeded = root.querySelector('[data-mdp-shipping-needed-input]');
    var inWeekLabel = root.querySelector('[data-mdp-week-label-input]');
    var inByLabel = root.querySelector('[data-mdp-deliverby-label-input]');
    var inDnod = root.querySelector('[data-mdp-dnod-input]');
    var dnodValue = inDnod ? inDnod.value : '';

    /* price lists, one per card (Addendum F6) */
    var optsEls = root.querySelectorAll('[data-xm-opts]');

    var ranges = [];        /* week mode: currently offered */
    var baseRanges = [];    /* week mode: unfiltered list; [] if /eta/ranges failed */
    var week = null;        /* week mode: selected {start, end, state} */
    var chosen = '';        /* date/birthday mode: chosen ISO date */
    var chosenState = '';   /* comfortable | tight | past | estimate | unavailable */
    var needService = '';   /* the service named in the tight line */
    var postal = '';        /* normalised postal that passed the engine */
    var arrivals = Object.create(null); /* CP service code -> ISO arrival */
    var fedexArrivals = Object.create(null); /* intuitive_code -> {arrival, by} */
    var belated = false;    /* week mode state 6 */
    var weekOpen = false;   /* week mode: "Change" pressed, buttons back, week kept */
    var postalEdit = false; /* card 2: "Change" pressed on a known postal code */
    /* ENGINE UNREACHABLE MEANS FAIL-OPEN (C, ruled 2026-09-22). Set, never
       cleared, for the page, when a lookup gets no data, the probe fails, or
       the load-time ranges call fails. An invalid_postal answer comes from a
       LIVE engine and never sets it. While it is true card 2 asks for no
       postal code. */
    var engineDown = false;
    /* True only when a postal LOOKUP (not the probe, not the ranges call)
       set engineDown. A later successful lookup clears both, so card 2 goes
       back to asking for the code and giving verdicts. */
    var lookupDown = false;
    var probeState = '';    /* '' not fired, 'pending', 'done'; one probe per page */
    var checkSeq = 0;
    var targetSeq = 0;
    /* Addendum I1 (built 2026-09-22): the answer and target the tight verdict
       was painted from, so the service name can be refined once the price
       rows land; the verdict itself; and the last ranked price rows, keyed
       exactly as fetchPrices caches them (variant|postal|iso). */
    var verdictCtx = null;
    var verdictNow = null;
    var lastRows = Object.create(null);

    /* ---------- the blanket day (Finding 6, ruled by Tommy 2026-09-21) ----------
       A personalized blanket costs the floor exactly one ship day. The engine
       applies it when a call carries personalized=1, so card 1, the date floor,
       the verdict and the price rows all move together.
       THE SIGNAL IS THE BLANKET'S OWN UPSELL CHECKBOX, verified in a browser on
       bunny-hugs 2026-09-21: one input[name="id[]"] inside the per-set block
       (_personalize_baby_boy / _girl / _neutral, twins bare _personalize_baby).
       It counts only when checked AND visible, so a hidden door of a
       cross-listed product never counts. NOT _blanket_on, which submits empty
       (Observation 7). Read-only: nothing inside #gsAppContainer is written. */
    var BLANKET_SEL = '#gsAppContainer [data-child-name^="_personalize_baby"] input[name="id[]"]';
    function blanketTicked() {
      var els = document.querySelectorAll(BLANKET_SEL);
      for (var i = 0; i < els.length; i++) {
        if (els[i].checked && els[i].offsetParent !== null) return true;
      }
      return false;
    }
    /* Read once at init, in case a restored page already has it ticked. */
    var personalized = blanketTicked();
    /* With the flag off every URL is exactly what it was before this change. */
    function flagged(url) {
      if (!personalized) return url;
      return url + (url.indexOf('?') === -1 ? '?' : '&') + 'personalized=1';
    }

    function setInput(el, value) {
      if (!el) return;
      if (value) { el.value = value; el.disabled = false; }
      else { el.value = ''; el.disabled = true; }
    }
    function show(el, on) { if (el) el.hidden = !on; }
    function selectRadio(r) {
      if (!r || r.checked) return;
      r.checked = true;
      r.dispatchEvent(new Event('change', { bubbles: true }));
    }
    function altMode() { return !!(radioAlt && radioAlt.checked); }

    /* ---------- the verdict (Addendum I1 and I4, built 2026-09-22) ----------
       ONE judgement for both blocks, across the SEVEN services the engine
       quotes. Rulings by Tommy 2026-09-22: a week is judged against its LAST
       day; FedEx counts; Canada Post names are the fallback when prices are
       missing. The engine's own state (targetState) is the BASE, because it
       knows the calendar and the quoting ceiling; this only widens it.
       I5 STANDS: order_by stays the Canada Post deadline and nothing here
       touches the schedule. No "guaranteed" wording anywhere (G6).
       A service MAKES the target when its arrival DATE is on or before it;
       arrival_by never ranks (R1). FREE is Expedited Parcel or FedEx Ground;
       PAID is the other five. I6 and I7 are NOT built: past still blocks.
       EIGHT since 2026-10-02: engine cb8dc723 publishes FedEx Priority
       Overnight Saturday Delivery, quoted only for a Friday ship day. It is
       a paid code like the rest; without it here the picker offered the
       Saturday and the verdict then called it past. */
    var FREE_CODES = ['DOM.EP', 'fedex_ground'];
    var PAID_CODES = ['DOM.XP', 'DOM.PC', 'fedex_express_saver', 'priority_overnight', 'first_overnight', 'priority_overnight_saturday_delivery'];
    /* Used ONLY when no price rows exist. A FedEx row with rows names the
       shadow cart's own title (G4); this map is joined by code, never by
       title (F17), and spells the checkout's names, not FedEx's. */
    var FX_NAME = {
      'fedex_express_saver': 'FedEx Express Saver',
      'priority_overnight': 'FedEx Priority Overnight',
      'first_overnight': 'FedEx First Overnight',
      'priority_overnight_saturday_delivery': 'FedEx Priority Overnight Saturday Delivery'
    };
    /* ONE CLEAN SERVICE NAME (ruled by Tommy 2026-09-23): the sentence and
       properties[Shipping needed] carry the SAME string, the checkout's method
       name without the symbol. Canada Post paid services get the carrier in
       front ("Canada Post Xpresspost", never a bare "Priority"); a FedEx
       service takes the shadow cart's own row title when a row is present,
       else FX_NAME. ® and ™ are stripped in every case. tightService() and
       PRETTY are left alone, and the cards and the list keep the shadow
       cart's own titles (G4, R4). */
    function serviceName(code, row) {
      var n = '';
      if (code === 'DOM.XP' || code === 'DOM.PC') n = 'Canada Post ' + PRETTY[code];
      else if (FX_NAME[code]) n = (row && row.opt && row.opt.title) ? String(row.opt.title) : FX_NAME[code];
      else if (PRETTY[code]) n = PRETTY[code];
      return n.replace(/[®™]/g, '').replace(/\s+/g, ' ').trim();
    }
    /* A Canada Post name the ENGINE supplied ("Priority"), turned into the
       clean name by EQUALITY with PRETTY, never by substring (F17). Anything
       else is returned with the symbols stripped. */
    function nameFromTitle(t) {
      for (var c in PRETTY) if (PRETTY[c] === t) return serviceName(c);
      return String(t || '').replace(/[®™]/g, '').replace(/\s+/g, ' ').trim();
    }
    function anyKey(o) { for (var k in o) return true; return false; }
    /* { code: ISO arrival } for the seven, from THIS answer, each carrier
       falling back to the page's own map when the answer carries none. */
    function sevenOf(data) {
      var cp = arrivalsOf(data);
      if (!anyKey(cp)) cp = arrivals;
      var fx = fedexArrivalsOf(data);
      if (!anyKey(fx)) fx = fedexArrivals;
      var out = Object.create(null), k;
      for (k in cp) if (SERVICE_ORDER.indexOf(k) !== -1 && parseIso(cp[k])) out[k] = cp[k];
      for (k in fx) {
        if (fx[k] && parseIso(fx[k].arrival) && (FREE_CODES.indexOf(k) !== -1 || PAID_CODES.indexOf(k) !== -1)) out[k] = fx[k].arrival;
      }
      return out;
    }
    function earliestOf(seven, codes) {
      var best = '';
      for (var i = 0; i < codes.length; i++) {
        var a = seven[codes[i]];
        if (a && (!best || a < best)) best = a;
      }
      return best;
    }
    function verdict(data, target) {
      var base = targetState(data);
      var seven = sevenOf(data);
      var v = {
        base: base, state: base, service: '',
        earliest: earliestOf(seven, FREE_CODES.concat(PAID_CODES)),
        earliestFree: earliestOf(seven, FREE_CODES),
        freeArrival: ''
      };
      if (!target || !base || base === 'estimate' || base === 'unavailable') return v;
      var freeMakers = FREE_CODES.filter(function (c) { return seven[c] && seven[c] <= target; });
      if (base === 'comfortable' || freeMakers.length) {
        v.state = 'comfortable';
        /* Widened by a free service the engine did not judge (FedEx Ground in
           practice): the page shows that service's own arrival, because the
           engine's planned/cushion pair belongs to its deadline service. */
        if (base !== 'comfortable') v.freeArrival = earliestOf(seven, freeMakers);
        return v;
      }
      var makers = PAID_CODES.filter(function (c) { return seven[c] && seven[c] <= target; });
      /* The engine's tight rests on a Canada Post paid arrival in this same
         answer, so it can never become past here. */
      if (!makers.length && base !== 'tight') { v.state = 'past'; return v; }
      v.state = 'tight';
      v.service = cheapestPaid(makers, seven, data, target, base);
      v.pick = tightPick(makers, seven, data, target, v.service);
      return v;
    }
    /* The CHEAPEST paid maker by the shadow-cart price, from the rows the
       price list last drew for this variant, postal code and date. Ties by
       arrival, then row order. Never refetches. null when no rows. */
    function bestRow(makers, target) {
      var rows = lastRows[variantId() + '|' + postal + '|' + target];
      if (!rows) return null;
      var best = null;
      for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        var key = r.code || String(r.opt.code || '');
        if (makers.indexOf(key) === -1 || !isFinite(r.amount)) continue;
        if (!best || r.amount < best.amount ||
            (r.amount === best.amount && (r.arrival || '9999') < (best.arrival || '9999'))) best = r;
      }
      return best;
    }
    function cheapestPaid(makers, seven, data, target, base) {
      var best = bestRow(makers, target);
      if (best) return serviceName(best.code || String(best.opt.code || ''), best);
      /* No rows. The engine's own naming is trusted only when its base is
         tight, because only then does it name a service that makes the date
         (on `past` it names the fastest one that does not). Code first. */
      if (base === 'tight') {
        var ob = (data && data.order_by) || {};
        var obCode = String(ob.service_code || '').toUpperCase();
        if (PRETTY[obCode] && makers.indexOf(obCode) !== -1) return serviceName(obCode);
        if (typeof ob.service === 'string' && ob.service && !/^dom\./i.test(ob.service)) return nameFromTitle(ob.service);
        var ts = tightService(data, sevenOf(data), target);
        if (ts) return nameFromTitle(ts);
      }
      /* The earliest paid maker, Canada Post first on a tie (PAID_CODES order). */
      var pick = '';
      for (var j = 0; j < makers.length; j++) {
        if (!pick || seven[makers[j]] < seven[pick]) pick = makers[j];
      }
      return pick ? serviceName(pick) : '';
    }
    /* ITEM 86, ruled by Tommy 2026-09-27: the tight sentence states the
       carrier's COMMITMENT for the service it names: its date, its time when
       the carrier gives one, and its live price. Same row as cheapestPaid()
       when rows exist. With no rows the service is the maker whose clean name
       is the one printed, else the earliest paid maker; no price then. The
       NAME is never changed here, so properties[Arrives by your date only if
       you select] carries exactly what it did before. */
    function tightPick(makers, seven, data, target, name) {
      var fx = fedexArrivalsOf(data);
      if (!anyKey(fx)) fx = fedexArrivals;
      var best = bestRow(makers, target);
      if (best) {
        var key = best.code || String(best.opt.code || '');
        return {
          arrival: best.arrival || seven[key] || '',
          by: best.fx ? best.fx.by : (fx[key] ? fx[key].by : ''),
          price: money(best.opt)
        };
      }
      var code = '', i;
      for (i = 0; i < makers.length; i++) if (serviceName(makers[i]) === name) code = makers[i];
      if (!code) {
        for (i = 0; i < makers.length; i++) {
          if (!code || seven[makers[i]] < seven[code]) code = makers[i];
        }
      }
      if (!code) return null;
      return { arrival: seven[code] || '', by: fx[code] ? fx[code].by : '', price: '' };
    }
    /* "FedEx Priority Overnight arrives Tue, Sep 29 by 12:00 PM. $66.95 at
       checkout." The time uses the cards' own label, so 23:59 reads "by end
       of day" (ruled 2026-09-27); no time, no "by". No price, no second
       sentence. A merchant string with the older tokens still fills. */
    function tightSentence(v) {
      var p = v.pick || {};
      var at = arrivalByLabel(p.by);
      var when = p.arrival ? dayShort(p.arrival) + (at ? ' ' + copy('card-by').replace('{time}', at) : '') : '';
      var head = tightLine('date-tight', v, { date: shortNoYear(chosen) }).split('{when}').join(when)
        .replace(/\s+\./g, '.').replace(/\s{2,}/g, ' ').trim();
      var price = p.price && p.price !== 'Free' ? p.price : '';
      var tail = price ? tightLine('date-tight-sub', v, { date: shortNoYear(chosen) }).split('{price}').join(price).trim() : '';
      return tail ? head + ' ' + tail : head;
    }
    /* ITEM 86 FOLLOW-UP, ruled by Tommy 2026-09-27 from his walk of staging:
       the tight case reads as TWO lines, the question and then the answer.
       Line 1, "Free shipping would arrive Fri, Oct 2, after your date.", the
       earliest FREE arrival in dayShort form; omitted when no free arrival is
       quoted, and the commitment sentence then stands alone as the head.
       Line 2 is tightSentence(), unchanged. [head, sub]. */
    function tightLines(v) {
      var t = v.earliestFree ? copy('date-tight-free') : '';
      var free = t ? t.split('{earliest_free}').join(dayShort(v.earliestFree)) : '';
      return free ? [free, tightSentence(v)] : [tightSentence(v), ''];
    }
    /* The date card's result lines. An empty sub-line or note is hidden, so
       an empty paragraph never leaves a gap. The always-on line under the
       field is shown or hidden by paintSteps(), which runs after this. */
    function setResult(head, sub, note) {
      if (dateHead) dateHead.textContent = head;
      if (dateSub) { dateSub.textContent = sub || ''; show(dateSub, !!sub); }
      if (dateNote) { dateNote.textContent = note || ''; show(dateNote, !!note); }
    }
    /* {earliest_free}: filled, or its sentence dropped when no free arrival
       is quoted (split at ". " boundaries). A string without the token is
       returned unchanged. */
    function withFree(text, iso) {
      if (!text || text.indexOf('{earliest_free}') === -1) return text;
      if (iso) return text.split('{earliest_free}').join(shortNoYear(iso));
      var parts = text.split('. ');
      var kept = [];
      for (var i = 0; i < parts.length; i++) {
        var s = i < parts.length - 1 ? parts[i] + '.' : parts[i];
        if (s.indexOf('{earliest_free}') === -1) kept.push(s);
      }
      return kept.join(' ');
    }
    /* The tight lines, date and week alike, from the current verdict. */
    function tightLine(key, v, extra) {
      var t = copy(key)
        .split('{service}').join(v.service || copy('faster-option'))
        .split('{date}').join(extra.date || '')
        .split('{week}').join(extra.week || '');
      return withFree(t, v.earliestFree);
    }
    /* Rows landed after the verdict was painted: refine ONLY the service name
       of a tight verdict. No syncInputs and no paintPrices, which would loop
       back through fetchPrices. */
    function refreshVerdictName() {
      if (chosenState !== 'tight' || !verdictCtx) return;
      var v = verdict(verdictCtx.data, verdictCtx.target);
      if (v.state !== 'tight' || !v.service) return;
      if (mode === 'week') {
        if (v.service === needService) return;
        verdictNow = v;
        needService = v.service;
        paintSteps();
        if (altMode() && week) setInput(inNeeded, needService);
      } else {
        /* Item 86: the rows also carry the price and the carrier's time, so
           the sentence repaints even when the name did not change. */
        verdictNow = v;
        needService = v.service;
        var tl = tightLines(v);
        setResult(tl[0], tl[1], '');
        if (altMode() && chosen) setInput(inNeeded, needService);
      }
    }

    /* ---------- card 2 steps (ruled 2026-09-22) ---------- */
    /* Calendar days from today in Toronto to an ISO date. */
    function daysUntil(iso) {
      var a = parseIso(todayToronto()), b = parseIso(iso);
      return (a && b) ? Math.round((b - a) / 86400000) : Infinity;
    }
    /* Rules C and D: card 2 needs a postal code only when the chosen week
       starts, or the chosen date falls, within leadDays. Set 999 to always. */
    function nearChoice() {
      if (mode === 'week') return !!week && daysUntil(week.start) <= leadDays;
      /* ITEM 76 (ruled by Tommy 2026-10-02): the date card asks for the
         postal code before the date, always, so the lead-days gate retires
         here and leadDays is read by the week card alone. It used to read
         `!!chosen && daysUntil(chosen) <= leadDays`. */
      if (mode === 'date') return true;
      return false;
    }
    /* C3: never while the engine is unreachable. */
    function postalRequired() { return !engineDown && nearChoice(); }
    /* C2: the first time a postal code would be asked for, check ONCE that
       the engine answers before showing the field. The bare ranges call makes
       no carrier call (handleRanges quotes only when `postal` is present) and
       ignores unknown query parameters, so the timestamp only defeats any
       cache. It never goes through getJson, whose cache would hand back an
       old answer. No usable answer within 4 s, or a non-2xx, means down. */
    var PROBE_MS = 4000;
    function maybeProbe() {
      if (probeState || engineDown || postal || !nearChoice()) return;
      /* Item 76: in date mode the ask is unconditional, so the probe fires
         when card 2 is SELECTED, not at page load; a shopper who never opens
         card 2 costs no call. */
      if (mode === 'date' && !altMode()) return;
      probeState = 'pending';
      var settled = false;
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      function settle(ok) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        probeState = 'done';
        if (!ok) { goDown(); return; }
        syncInputs();
      }
      var timer = setTimeout(function () { settle(false); if (ctrl) ctrl.abort(); }, PROBE_MS);
      var init = { method: 'GET', mode: 'cors', credentials: 'omit', cache: 'no-store' };
      if (ctrl) init.signal = ctrl.signal;
      fetch(rangesUrl + '?probe=' + Date.now(), init)
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) { settle(!!validRanges(data)); })
        .catch(function () { settle(false); });
    }
    /* C4 and C5: the engine is unreachable. Card 2 repaints at once, the
       field she was typing in disappears, and a chosen date takes the
       estimate pair. Card 1 is left exactly as it is. */
    function goDown() {
      engineDown = true;
      postalEdit = false;
      setErr(fieldErr, '');
      if (mode === 'date' && chosen) { onDatePicked(); return; }
      /* C5: no Shipping needed while the engine is down; retire any week
         verdict still in flight. */
      if (mode === 'week') { ++targetSeq; chosenState = ''; needService = ''; verdictCtx = null; verdictNow = null; }
      syncInputs();
    }
    function setErr(el, key) {
      if (!el) return;
      var t = el.querySelector('[data-xm2-err-text]');
      if (t) t.textContent = key ? copy(key) : '';
      el.hidden = !key;
    }
    function clearErrors() {
      setErr(cardErr, '');
      setErr(fieldErr, '');
      setErr(msgEl, '');
    }
    /* What card 2 shows, from `week`, `chosen` and `postal`. One step at a
       time: the week buttons, or the week summary and then either card 2's
       postal field or the result. The date field itself never collapses. */
    function paintSteps() {
      if (mode === 'birthday') return;
      maybeProbe();
      /* While the probe is out, nothing shows below the week or date. */
      var probing = probeState === 'pending';
      var need = postalRequired();
      var askPostal = need && (!postal || postalEdit);
      if (mode === 'week') {
        var collapsed = !!week && !weekOpen;
        show(weeksEl, !collapsed);
        show(weekSum, collapsed);
        if (week && weekSumText) weekSumText.textContent = copy('week-summary').replace('{week}', rangeLabel(week.start, week.end));
        /* I1: a week still tight on its LAST day names the paid service. */
        var weekTight = !!(week && chosenState === 'tight' && needService && verdictNow);
        if (week && weekHead) {
          weekHead.textContent = weekTight
            ? tightLine('week-tight', verdictNow, { week: rangeLabel(week.start, week.end) })
            : copy('week-est') + ' ' + rangeLabel(week.start, week.end) + (week.state === 'estimate' ? ' (' + copy('estimate') + ')' : '') + '.';
        }
        if (weekSub) {
          var subText = weekTight ? tightLine('week-tight-sub', verdictNow, { week: rangeLabel(week.start, week.end) }) : '';
          weekSub.textContent = subText;
          show(weekSub, !!subText);
        }
        show(postal2Form, collapsed && askPostal && !probing);
        show(postalSum, collapsed && need && !askPostal);
        show(weekResult, collapsed && !askPostal);
        show(confirmAfterEl, engineDown);
      } else {
        /* ITEM 76 (ruled by Tommy 2026-10-02): the postal step comes FIRST.
           With no code known the step shows above the date field (hidden
           only while the probe is out), the date input is DISABLED and the
           helper line shows. With a code known, and not being changed, the
           summary shows instead, the date is enabled and the helper goes.
           `need` is false only while the engine is unreachable (fail-open,
           C): then nothing is asked and the date is enabled on the no-postal
           floor, as before. Before this the two show() calls were gated on
           `!!chosen`, so the ask came AFTER a date was picked. */
        var locked = askPostal;
        show(postal2Form, askPostal && !probing);
        show(postalSum, need && !askPostal);
        if (dateEl) dateEl.disabled = locked;
        show(dateHelp, locked);
        if (!chosen || askPostal) show(dateResult, false);
        /* Item 86 case 1 (ruled 2026-09-27, option B): while the comfortable
           result shows its own "Need it sooner?" line, the always-on line is
           hidden; everywhere else it shows unchanged.
           RETIRED 2026-09-27, ruled by Tommy from his walk of staging: on a
           comfortable date BOTH lines showed. The always-on line is gone from
           card 2 in EVERY state and its markup is removed from the snippet;
           this stays only so a stray copy of the old markup is kept hidden.
           Case 1 keeps its own "Need it sooner?" line. Do not bring it back. */
        show(alwaysNote, false);
      }
      if (postal && postalSumText) postalSumText.textContent = copy('postal-summary').replace('{postal}', displayPostal(postal));
    }

    /* ---------- the properties (Addendum E1) ---------- */
    function syncInputs() {
      var timing = 'ASAP', from = '', by = '', weekLabel = '', byLabel = '', needed = '';

      if (mode === 'week' && altMode() && week) {
        timing = 'WEEK';
        from = week.start;
        by = week.end;
        weekLabel = rangeLabelWithYear(week.start, week.end);
        if (chosenState === 'tight' && needService) needed = needService;
      } else if (mode !== 'week' && altMode() && chosen) {
        /* F4: a blank date on card 2 stays ASAP and submits no date at all. */
        timing = 'DATE';
        from = chosen;
        by = chosen;
        byLabel = shortWithYear(chosen);
        if (chosenState === 'tight' && needService) needed = needService;
      }

      if (inTiming) { inTiming.value = timing; inTiming.disabled = false; }
      setInput(inFrom, from);
      setInput(inBy, by);
      setInput(inDate, from);
      setInput(inWeekLabel, weekLabel);
      setInput(inByLabel, byLabel);
      setInput(inNeeded, needed);
      /* Rule C: a week too far out to need a postal code does not carry one
         on the order, even when card 1 happens to know it. */
      var postalUsed = postal ? displayPostal(postal) : '';
      if (engineDown) {
        /* C4: a well-formed code already in hand stays; anything else drops. */
        postalUsed = /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(postal) ? displayPostal(postal) : '';
      } else if (timing === 'WEEK' && !postalRequired()) {
        postalUsed = '';
      }
      setInput(inPostal, postalUsed);

      /* Christmas owns the sticker; birthday's Do Not Open Until belongs to
         initReserve() in occasion-dated-product-page.js and is not touched. */
      if (mode === 'week' && inDnod) {
        var on = !belated && stickerCheck && stickerCheck.checked;
        inDnod.value = on ? dnodValue : '';
        inDnod.disabled = !on;
      }

      root.setAttribute('data-xm-state', belated ? 'belated' : (timing === 'ASAP' ? (postal ? 'postal' : 'before') : timing.toLowerCase()));
      paintSteps();
      paintPrices();
    }

    /* ---------- shadow-cart price list (E3, F6, F7) ---------- */
    function attributesNow() {
      var out = [];
      function push(k, v) { if (v) out.push({ key: k, value: v }); }
      push('_Ship Timing', inTiming ? inTiming.value : '');
      if (inFrom && !inFrom.disabled) push('_Deliver from', inFrom.value);
      if (inBy && !inBy.disabled) push('_Deliver by', inBy.value);
      if (inDate && !inDate.disabled) push('_date', inDate.value);
      return out;
    }
    function variantId() {
      var el = form && form.querySelector('[data-mdp-variant-id]');
      return el ? String(el.value || '').replace(/\D/g, '') : '';
    }
    function paintPrices() {
      if (!optsEls.length) return;
      var vid = variantId();
      if (!token || !postal || !vid) { for (var i = 0; i < optsEls.length; i++) show(optsEls[i], false); return; }
      /* ITEM 87, ruled by Tommy 2026-09-27: a comfortable DATE verdict on
         card 2 shows no cards and no Compare link (renderPrices, item 86), so
         the shadow-cart price request is skipped entirely, fewer carrier-rate
         calls per visitor. Keyed on the SETTLED chosenState, which paintDate()
         always sets before calling syncInputs(), so this never races an
         in-flight engine call. altMode() keeps card 1 untouched even though
         chosen/chosenState survive a switch back to it from a comfortable
         date; mode === 'date' keeps the Christmas week card untouched. The
         hide loop matters here: without it a STALE tight-verdict card list
         from the previous date would keep showing under the new comfortable
         headline, because renderPrices() never runs to clear it. */
      if (mode === 'date' && altMode() && chosenState === 'comfortable') {
        for (var i = 0; i < optsEls.length; i++) show(optsEls[i], false);
        return;
      }
      /* Week mode tags against the week's LAST day (ruled 2026-09-22), so the
         "in time" tags and the tight sentence judge the same day. */
      var iso = (mode === 'week' && week) ? week.end : chosen;
      var rowsKey = vid + '|' + postal + '|' + (iso || '');
      fetchPrices({ token: token, postal: postal, variantId: vid, iso: iso, attributes: attributesNow() })
        .then(function (opts) { renderPrices(opts, iso, rowsKey); });
    }
    function renderPrices(opts, iso, rowsKey) {
      var ranked = opts ? rankRows(opts) : null;
      /* I1: the tight verdict names the cheapest paid maker from these rows. */
      if (ranked && rowsKey) { lastRows[rowsKey] = ranked; refreshVerdictName(); }
      var cards = ranked ? summaryCards(ranked) : [];
      for (var i = 0; i < optsEls.length; i++) {
        var host = optsEls[i];
        var list = host.querySelector('[data-xm-opts-list]');
        if (!list) continue;
        if (!ranked) { show(host, false); continue; }

        /* ONE LIST AT A TIME, INSIDE THE SELECTED CARD ONLY. Both cards carry a
           host, and this loop used to fill and show BOTH, so the ASAP card sat
           there wearing the date card's "in time" judgements. `.mdp-xm__card--asap`
           exists in both the date and the Christmas snippet, and altMode() already
           tracks which card is chosen, so one selector covers both blocks. */
        var isAsap = !!host.closest('.mdp-xm__card--asap');
        if (isAsap === altMode()) { show(host, false); continue; }
        /* ITEM 86, ruled by Tommy 2026-09-27: on card 2, date and week alike,
           the cards and the Compare link show ONLY when the verdict is tight,
           that is free ground misses and a paid service makes it. Comfortable,
           past, estimate and no verdict at all show neither. The rows above
           are still ranked and kept in lastRows, which the tight sentence
           reads, so nothing about the verdict changes. Card 1 is untouched. */
        if (!isAsap && chosenState !== 'tight') { show(host, false); continue; }
        /* CARD 1 NEVER JUDGES A DATE. The skip above already hides it while card 2
           is selected, so this looks redundant, and it is not: `chosen` survives a
           switch back to card 1, and without this the ASAP list would show stale
           in-time tags against a date the shopper is no longer asking for. */
        var hostIso = isAsap ? '' : iso;

        list.innerHTML = '';
        rowsFor(ranked, hostIso).forEach(function (row) { list.appendChild(row); });

        /* The cards sit above the list and the list moves into a collapsed
           disclosure. Both are optional in the markup: a snippet that has not
           been updated still renders the plain list exactly as before. */
        var cardsEl = host.querySelector('[data-xm-cards]');
        if (cardsEl) {
          cardsEl.innerHTML = '';
          cards.forEach(function (c) { cardsEl.appendChild(cardEl(c, hostIso)); });
          show(cardsEl, cards.length > 0);
        }
        var note = host.querySelector('[data-xm-cards-note]');
        if (note) show(note, cards.length > 0);
        var sum = host.querySelector('[data-xm-compare-sum]');
        if (sum) sum.textContent = copy('compare-all').replace('{n}', String(list.childNodes.length));
        var comp = host.querySelector('[data-xm-compare]');
        if (comp) show(comp, list.childNodes.length > 0);

        show(host, list.childNodes.length > 0);
      }
    }
    /* ORDER, ruled by Tommy 2026-09-20 and amended the same day:
         1. Expedited Parcel     (free ground, Canada Post)
         2. FedEx Ground         (the other free option)
         3. everything else, by ARRIVAL DATE, then by PRICE ascending.
       The amendment is the last clause: within one arrival date the cheaper
       option leads. TIME IS DISPLAYED ON ROWS THAT CARRY IT BUT NEVER DRIVES
       THE ORDER, which is what keeps a $134 overnight from outranking an $8
       option that lands the same day.

       A row with no arrival date at all sorts to the very end, because '9999'
       exceeds any real ISO date, and among those the incoming order is kept.
       That is the Iqaluit case: Canada Post Priority is not quoted there and
       neither are two of the FedEx services, so the list is simply shorter.

       SUPERSEDES the earlier F6 reading, "free ground first with its arrival,
       then the express options", which had no tie-break at all and left the
       remaining rows in whatever order the shadow cart happened to return. */
    var LEAD = { 'DOM.EP': 0, 'fedex_ground': 1 };
    /* One pass over the shadow-cart options, shared by the list and by the
       summary cards, so the two can never disagree about a date or a price. */
    function rankRows(opts) {
      var ranked = [];
      for (var i = 0; i < opts.length; i++) {
        var o = opts[i];
        var code = codeOf(o);
        var fx = fedexArrivals[String(o.code || '')];
        var arrival = (code && arrivals[code]) || (fx && fx.arrival) || '';
        var lead = LEAD[code] !== undefined ? LEAD[code]
                 : (LEAD[String(o.code || '')] !== undefined ? LEAD[String(o.code || '')] : 9);
        ranked.push({
          opt: o, code: code, fx: fx || null, arrival: arrival,
          lead: lead, amount: priceAmount(o), seq: i
        });
      }
      ranked.sort(function (a, b) {
        if (a.lead !== b.lead) return a.lead - b.lead;
        var ad = a.arrival || '9999-99-99', bd = b.arrival || '9999-99-99';
        if (ad !== bd) return ad < bd ? -1 : 1;
        if (a.amount !== b.amount) return a.amount - b.amount;
        return a.seq - b.seq;
      });
      return ranked;
    }
    function rowsFor(ranked, iso) {
      return ranked.map(function (r) { return priceRow(r, iso); }).filter(Boolean);
    }

    /* ---------- summary cards (step 1d) ----------
       Up to three, computed from the SAME ranked rows the list uses. Every rule
       below is Tommy's, ruled 2026-09-20 at Stop Point 1, and the reasons are
       recorded because two of them overturned what the first draft did.

       FREE DELIVERY: the free option with the earliest arrival, ties to
       Expedited Parcel.
       GET IT EARLIER: the CHEAPEST paid option whose arrival DATE is strictly
       earlier than the free card's. Omitted when none is.
       EARLIEST DELIVERY: earliest arrival DATE, and within that date the
       CHEAPEST option, which is the same tie-break the list uses (R1).
       TIME OF DAY NEVER RANKS. An earlier draft sorted this card by
       `arrival_by`, which had no answer for Canada Post rows (they carry no
       commitment time) and put FedEx First Overnight at $278.58 above Canada
       Post Priority at $73.94 on the same Edmonton day.
       SUPPRESSED when its date equals the free card's date (R2): same-day-as-
       free is not an earlier delivery, which is why Toronto shows one card.
       Also suppressed when it would repeat the "get it earlier" row.
       Rows with no arrival date are excluded from every card.
       NO SATURDAY CARD (ruled by Tommy 2026-10-02, supersedes the weekend-
       earliest substitution this replaced): priority_overnight_saturday_
       delivery never takes a card slot, free, "get it earlier" or earliest,
       because Tommy does not want a rare, expensive service competing for
       one. It is excluded from `dated` below, before any of the three picks
       run, so it cannot win free (it is never $0), "get it earlier" or
       earliest. It stays in the Compare list (rowsFor uses the full `ranked`,
       not `dated`) and stays fully usable on card 2 when a shopper picks its
       date directly.
       The weekend-earliest substitution the comment above used to describe
       is REMOVED, not kept dormant: it existed only to bump the Saturday
       service out of the earliest slot when a cheaper weekday option beat
       it, and with the Saturday service excluded from `dated` outright,
       nothing else on this engine quotes a Saturday or Sunday arrival (every
       other PAID_CODES and FREE_CODES entry ships on a weekday, per the
       ship-date rollover in nextWeekdayAfter), so the branch had no
       remaining case that could reach it. */
    function summaryCards(ranked) {
      var dated = ranked.filter(function (r) {
        return !!r.arrival && (!r.opt || r.opt.code !== 'priority_overnight_saturday_delivery');
      });
      var free = null;
      dated.forEach(function (r) {
        if (r.amount !== 0) return;
        if (!free) { free = r; return; }
        if (r.arrival < free.arrival) { free = r; return; }
        if (r.arrival === free.arrival && r.code === 'DOM.EP') free = r;
      });
      var earlier = null;
      if (free) {
        dated.forEach(function (r) {
          if (r.amount <= 0 || r.arrival >= free.arrival) return;
          if (!earlier || r.amount < earlier.amount) earlier = r;
        });
      }
      var earliest = null;
      dated.forEach(function (r) {
        if (!earliest) { earliest = r; return; }
        if (r.arrival < earliest.arrival) { earliest = r; return; }
        if (r.arrival === earliest.arrival && r.amount < earliest.amount) earliest = r;
      });
      if (earliest && free && earliest.arrival === free.arrival) earliest = null;
      if (earliest && earlier && earliest.opt === earlier.opt) earliest = null;
      return [
        { key: 'free', label: copy('card-free'), row: free },
        { key: 'earlier', label: copy('card-earlier'), row: earlier },
        { key: 'earliest', label: copy('card-earliest'), row: earliest }
      ].filter(function (c) { return !!c.row; });
    }
    /* R4: the shadow cart names the carrier in every title except `dom.ep`,
       which comes back as a bare "Expedited Parcel". The CARDS prepend the
       carrier so the most important card does not read as a nameless service.
       THE FULL LIST IS LEFT ALONE and still shows the shadow cart's own title,
       which is what checkout will say. */
    function cardName(r) {
      var t = String(r.opt.title || '');
      if (r.code && !/canada\s*post/i.test(t)) return 'Canada Post ' + t;
      return t;
    }
    function logoFor(r) {
      var t = String(r.opt.title || '').toLowerCase();
      if (fedexLogo && t.indexOf('fedex') !== -1) return fedexLogo;
      if (cpLogo && r.code) return cpLogo;
      return '';
    }
    function cardEl(card, iso) {
      var r = card.row;
      var li = document.createElement('li');
      li.className = 'mdp-xm__card-pick mdp-xm__card-pick--' + card.key;

      var lab = document.createElement('span');
      lab.className = 'mdp-xm__card-pick-label';
      lab.textContent = card.label;
      li.appendChild(lab);

      var when = document.createElement('span');
      when.className = 'mdp-xm__card-pick-when';
      var at = r.fx ? arrivalByLabel(r.fx.by) : '';
      when.textContent = weekdayShort(r.arrival) + (at ? ' ' + copy('card-by').replace('{time}', at) : '');
      li.appendChild(when);

      /* Card 2 carries the same in-time judgement as the list rows. */
      if (iso) {
        var tag = document.createElement('span');
        tag.className = 'mdp-xm__card-pick-tag';
        tag.textContent = r.arrival <= iso ? copy('in-time') : copy('not-in-time').replace('{date}', shortNoYear(iso));
        li.appendChild(tag);
      }

      var foot = document.createElement('span');
      foot.className = 'mdp-xm__card-pick-foot';
      var cost = document.createElement('span');
      cost.className = 'mdp-xm__card-pick-price';
      cost.textContent = money(r.opt);
      foot.appendChild(cost);
      var who = document.createElement('span');
      who.className = 'mdp-xm__card-pick-name';
      var src = logoFor(r);
      if (src) {
        var img = document.createElement('img');
        img.className = 'mdp-xm__opt-logo';
        img.src = src;
        img.alt = '';
        who.appendChild(img);
      }
      who.appendChild(document.createTextNode(cardName(r)));
      foot.appendChild(who);
      li.appendChild(foot);
      return li;
    }
    function priceRow(r, iso) {
      var price = money(r.opt);
      if (!price) return null;
      var li = document.createElement('li');
      li.className = 'mdp-xm__opt';
      var left = document.createElement('span');

      var name = document.createElement('span');
      name.className = 'mdp-xm__opt-name';
      var title = String(r.opt.title || '').toLowerCase();
      if (fedexLogo && title.indexOf('fedex') !== -1) {
        var img = document.createElement('img');
        img.className = 'mdp-xm__opt-logo';
        img.src = fedexLogo;
        img.alt = '';
        name.appendChild(img);
      }
      name.appendChild(document.createTextNode(r.opt.title || ''));
      left.appendChild(name);

      /* Three shapes, in order of preference.
         1. A Canada Post row, joined to the engine by service code.
         2. A FedEx row, joined to the engine by `intuitive_code` (G3). It reads
            like a Canada Post row but carries the weekday and the commitment
            time, which is what separates two services that land the same day
            (G6). No "guaranteed" language anywhere: FedEx's guarantee flag is a
            billing remedy, not a delivery promise.
         3. Anything with no engine date: the E12 "faster option" label. That is
            now the FALLBACK rather than the plan (G7), and it is what a lane
            like Iqaluit shows for the services FedEx does not serve there.
         Card 2's in-time / not-in-time tag applies to BOTH dated shapes,
         judged against the chosen date. */
      var tagText = '';
      var dated = '';
      if (r.code && arrivals[r.code]) {
        dated = arrivals[r.code];
        tagText = ' ' + copy('arrives-on').replace('{date}', shortNoYear(dated));
      } else if (r.fx && r.fx.arrival) {
        dated = r.fx.arrival;
        var when = weekdayShort(dated);
        var at = arrivalByLabel(r.fx.by);
        tagText = ' ' + copy('arrives-on').replace('{date}', when + (at ? ' by ' + at : ''));
      }
      if (dated) {
        if (iso) tagText += ' ' + (dated <= iso ? copy('in-time') : copy('not-in-time').replace('{date}', shortNoYear(iso)));
      } else {
        var est = carrierEstimate(r.opt);
        tagText = ' ' + copy('faster-option') + (est ? ' ' + copy('arrives-on').replace('{date}', est) : '');
      }
      if (tagText) {
        var tag = document.createElement('span');
        tag.className = 'mdp-xm__opt-tag';
        tag.textContent = tagText;
        left.appendChild(tag);
      }

      var cost = document.createElement('span');
      cost.className = 'mdp-xm__opt-price';
      cost.textContent = price;
      li.appendChild(left);
      li.appendChild(cost);
      return li;
    }

    /* ---------- week mode: the week buttons ---------- */
    function renderWeeks() {
      if (!weeksEl) return;
      weeksEl.innerHTML = '';
      ranges.forEach(function (r) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'mdp-xm__week';
        b.setAttribute('data-mdp-week', '');
        b.setAttribute('data-start', r.start);
        b.setAttribute('data-end', r.end);
        var sel = !!(week && week.start === r.start && week.end === r.end);
        b.setAttribute('aria-pressed', sel ? 'true' : 'false');
        var label = document.createElement('span');
        label.textContent = rangeLabel(r.start, r.end);
        b.appendChild(label);
        if (r.state === 'estimate') {
          var tag = document.createElement('span');
          tag.className = 'mdp-xm__week-tag';
          tag.textContent = copy('estimate');
          b.appendChild(tag);
        }
        b.addEventListener('click', function () { chooseWeek(r); });
        weeksEl.appendChild(b);
      });
    }
    /* The result line and its visibility now live in paintSteps(), which
       syncInputs() runs, so a chosen week collapses to its summary line and
       card 2 asks for a postal code only when rule C says it must. */
    function chooseWeek(r) {
      week = r;
      weekOpen = false;
      selectRadio(radioAlt);
      setErr(cardErr, '');
      renderWeeks();
      weekVerdict();
      syncInputs();
    }
    /* Card 2 disappears when no range survives or the shipment is belated. */
    function applyRanges(list) {
      ranges = list;
      if (week && !ranges.some(function (r) { return r.start === week.start && r.end === week.end; })) week = null;
      if (week) week = ranges.filter(function (r) { return r.start === week.start && r.end === week.end; })[0];
      var hideCard = belated || ranges.length === 0;
      show(weekCard, !hideCard);
      if (hideCard) { week = null; selectRadio(radioAsap); }
      renderWeeks();
      weekVerdict();
      syncInputs();
    }
    /* I1 in week mode, ruled 2026-09-22: a week is judged against its LAST
       day. The ranges call already ran the engine's walk against the week
       START (README step 2a), and a week comfortable at its start cannot be
       tight at its end, so only a start-tight week asks again, once, with
       target = week.end. Clears the old verdict at once; the callers run
       syncInputs() themselves, and the answer runs it again when it lands.
       Tight then comfortable is EXPECTED (the end is later) and silent. */
    function weekVerdict() {
      var seq = ++targetSeq;
      chosenState = '';
      needService = '';
      verdictCtx = null;
      verdictNow = null;
      if (mode !== 'week' || !week || !postal || engineDown || week.state !== 'tight') return;
      var target = week.end;
      getJson(flagged(endpoint + '?postal=' + encodeURIComponent(postal) + '&target=' + encodeURIComponent(target))).then(function (data) {
        if (seq !== targetSeq || engineDown) return;
        var fxRefresh = fedexArrivalsOf(data);
        if (anyKey(fxRefresh)) fedexArrivals = fxRefresh;
        var v = verdict(data, target);
        if (v.state === 'tight') {
          chosenState = 'tight';
          needService = v.service;
          verdictCtx = { data: data, target: target };
          verdictNow = v;
        } else if (v.state === 'comfortable') {
          chosenState = 'comfortable';
        } else {
          console.error('[ship-timing] week ' + week.start + ' to ' + week.end +
            ' is tight at its start on /eta/ranges, but its last day judged "' + (v.state || 'nothing') +
            '"; no Shipping needed written. Ranges answer:', week, 'Target answer:', data);
        }
        syncInputs();
      });
    }
    /* D2, ruled 2026-09-22, SUPERSEDING the 2026-09-19 "a failed call gives
       [] and hides card 2". An EMPTY answer is the season being over and
       still hides card 2. A FAILURE (network error, non-2xx, unparseable, or
       an answer that is not an ok ranges list) sets engineDown and falls back
       to the block's own list, xm_fallback_weeks; if that parses to nothing,
       card 2 hides as before. Labels are still computed from the ISO pairs. */
    function loadBaseRanges() {
      return getJson(flagged(rangesUrl)).then(function (data) {
        var list = validRanges(data);
        if (list) return list;
        engineDown = true;
        return fallbackRanges();
      });
    }
    /* "START:END" pairs in YYYY-MM-DD, one per line (any whitespace or comma
       separates them). Invalid pairs are dropped, and so is every week the
       engine would not offer today: THE ENGINE'S OWN TRIM, mirrored exactly
       from offeredRanges() in glitter-eta-engine src/ranges.js, keeps a range
       only if start >= today + LEAD_DAYS (2 calendar days, Toronto), so the
       fallback and the engine always agree. That also covers "end before
       today", which D2 first asked for. */
    var FALLBACK_LEAD_DAYS = 2;
    function fallbackRanges() {
      var raw = root.getAttribute('data-fallback-weeks') || '';
      var earliestStart = addDays(todayToronto(), FALLBACK_LEAD_DAYS);
      var out = [];
      raw.split(/[\s,]+/).forEach(function (pair) {
        var m = /^(\d{4}-\d{2}-\d{2}):(\d{4}-\d{2}-\d{2})$/.exec(pair);
        if (!m || !parseIso(m[1]) || !parseIso(m[2]) || m[2] < m[1]) return;
        if (m[1] < earliestStart) return;
        out.push({ start: m[1], end: m[2], state: null });
      });
      return out;
    }
    /* Only `past` removes a range; `unavailable` stays offered (ruled
       2026-09-19). If this call fails, the base list stays. */
    function rangesForPostal(p) {
      /* D4: never while the engine is unreachable. */
      if (engineDown) return Promise.resolve(baseRanges);
      return getJson(flagged(rangesUrl + '?postal=' + encodeURIComponent(p))).then(function (data) {
        var list = validRanges(data);
        if (!list) return baseRanges;
        return list.filter(function (r) { return r.state !== 'past'; });
      });
    }

    /* ---------- date mode: the date field ---------- */
    /* F5: the floor is the earliest arrival across ALL quoted services, so a
       date only Xpresspost can reach stays pickable and the "free shipping
       will not make it" line is reachable. Today plus two until a postal code
       is known. SUPERSEDED 2026-09-28, see noPostalFloor() below: until a
       postal code is known the floor is the earliest arrival any lane could
       reach. */
    /* A chosen date below the new floor is KEPT (ruled 2026-09-21). This used
       to clear `chosen` and the field, so ticking the blanket, which raises
       the floor a day, emptied the box and check() then had no date to re-ask
       about. Now check() re-asks, the engine answers `past`, and the page
       shows the past line for the date the shopper picked. `chosen` is the
       source of truth: if a browser ever drops a value below `min`, it is
       written back. */
    /* ITEM 86, ruled by Tommy 2026-09-28: before a postal code is known the
       floor is the EARLIEST ARRIVAL ANY LANE COULD REACH, the next business
       day after the next ship opportunity, instead of today plus two. The
       ship opportunity mirrors shipDecision() in glitter-eta-engine
       src/earliest-ship.js: today when it is a ship day and the Toronto clock
       is STRICTLY before 12:00, else the next ship day; ship days are Monday
       to Friday. The arrival is the next Monday-to-Friday after that. Once a
       postal code is known the engine's earliest arrival replaces this, as
       before, and a typed date below it still gets case 3 from the engine.
       KNOWN LIMIT, accepted: the engine's closed dates live in its KV and are
       not visible here, so on a closed day this floor can read a day early.
       A date that near always asks for the postal code (rule D), and the
       engine then sets the true floor. */
    /* ITEM 76, ruled by Tommy 2026-10-02: the date input is DISABLED until a
       postal code is known (paintSteps), so the no-postal floor below is
       never the floor of a calendar a shopper can open. It still matters in
       one state, the engine-unreachable fail-open (C), where no code is asked
       and the date is enabled. That was the 2026-10-02 defect: a Toronto
       shopper opened the calendar on this floor and did not see Saturday. */
    var SHIP_CUTOFF_MIN = 12 * 60;
    function isWeekdayIso(iso) {
      var d = parseIso(iso);
      var w = d ? d.getUTCDay() : 0;
      return w >= 1 && w <= 5;
    }
    function nextWeekdayAfter(iso) {
      var d = addDays(iso, 1);
      while (d && !isWeekdayIso(d)) d = addDays(d, 1);
      return d;
    }
    function torontoMinutes() {
      try {
        var parts = new Intl.DateTimeFormat('en-CA', {
          timeZone: 'America/Toronto', hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
        }).formatToParts(new Date());
        var h = NaN, m = NaN;
        for (var i = 0; i < parts.length; i++) {
          if (parts[i].type === 'hour') h = Number(parts[i].value);
          if (parts[i].type === 'minute') m = Number(parts[i].value);
        }
        if (isFinite(h) && isFinite(m)) return h * 60 + m;
      } catch (e) {}
      var n = new Date();
      return n.getHours() * 60 + n.getMinutes();
    }
    function noPostalFloor() {
      var today = todayToronto();
      var ship = (isWeekdayIso(today) && torontoMinutes() < SHIP_CUTOFF_MIN) ? today : nextWeekdayAfter(today);
      return nextWeekdayAfter(ship) || addDays(today, 2);
    }
    function applyMinDate() {
      if (!dateEl || mode === 'birthday') return;
      /* I4 (built 2026-09-22): the floor reads FedEx too, so a date only a
         FedEx service can reach stays pickable. */
      var all = Object.create(null), k;
      for (k in arrivals) all[k] = arrivals[k];
      for (k in fedexArrivals) if (fedexArrivals[k] && parseIso(fedexArrivals[k].arrival)) all['fx:' + k] = fedexArrivals[k].arrival;
      var floor = earliestArrival(all) || noPostalFloor();
      dateEl.min = floor;
      if (chosen && dateEl.value !== chosen) dateEl.value = chosen;
    }
    function onDatePicked() {
      chosen = dateEl ? (dateEl.value || '') : '';
      setErr(cardErr, '');
      if (!chosen) { chosenState = ''; needService = ''; show(dateResult, false); syncInputs(); return; }
      selectRadio(radioAlt);

      if (mode === 'birthday') { chosenState = ''; needService = ''; syncInputs(); return; }
      if (engineDown) {
        /* C4: the estimate line with the calm line under it, no engine call,
           state `estimate`, no Shipping needed, nothing blocked. */
        ++targetSeq;
        needService = '';
        chosenState = 'estimate';
        setResult(copy('date-estimate').replace('{date}', shortNoYear(chosen)), copy('confirm-after'), '');
        show(dateResult, true);
        syncInputs();
        return;
      }
      if (!postal) {
        ++targetSeq;
        needService = '';
        /* Rule D, near date: card 2 shows its own postal step (paintSteps).
           Item 76: in date mode this is now EVERY date with the engine up
           (nearChoice is true), reached only by a date the browser restored
           or one kept across Change; the far-date estimate pair below is
           unreachable in date mode and is kept as the record. */
        if (postalRequired()) { chosenState = ''; show(dateResult, false); syncInputs(); return; }
        /* Rule D, far date and no postal code: the estimate pair, no engine
           call, state `estimate`, no Shipping needed, nothing blocked. */
        chosenState = 'estimate';
        setResult(copy('date-estimate').replace('{date}', shortNoYear(chosen)), copy('date-estimate-sub'), '');
        show(dateResult, true);
        syncInputs();
        return;
      }

      var seq = ++targetSeq;
      getJson(flagged(endpoint + '?postal=' + encodeURIComponent(postal) + '&target=' + encodeURIComponent(chosen))).then(function (data) {
        if (seq !== targetSeq) return;
        paintDate(data);
      });
    }
    function paintDate(data) {
      /* The target call returns the same `fedex` key, so refresh the map from
         it. Only ever replace a non-empty answer: a target call that fails
         must not wipe dates the postal call already produced.
         SUPERSEDED 2026-09-22 by Addendum I1: this comment used to end "FedEx
         NEVER feeds the comfortable/tight states, order_by or Shipping needed
         below; those stay Canada Post only". FedEx now feeds the STATE and
         Shipping needed through verdict(); order_by is still Canada Post only
         (I5). */
      var fxRefresh = fedexArrivalsOf(data);
      for (var fxKey in fxRefresh) { fedexArrivals = fxRefresh; break; }
      var v = verdict(data, chosen);
      var state = v.state || 'estimate';
      chosenState = state;
      needService = '';
      verdictCtx = null;
      verdictNow = v;
      var head = '', sub = '', note = '';

      if (state === 'comfortable') {
        /* ITEM 86 CASE 1, ruled by Tommy 2026-09-27: free ground makes the
           date. "Estimated arrival by <the CHOSEN date>", never the engine's
           cushion date, then the one "ship a day early" line and the case-1
           "Need it sooner?" line. No cards and no Compare link (renderPrices).
           TOMBSTONE: this replaces the two-date "{before} or {date}" form from
           order_by.planned_arrival / cushion_arrival (F5, 2026-09-21), and the
           FedEx Ground branch that printed its own arrival through
           date-single. Both could name a day AFTER the chosen date (item 81).
           Do not bring either back. */
        head = copy('date-comfortable').split('{date}').join(weekdayShort(chosen));
        /* ITEM 86, ruled by Tommy 2026-09-27 from the scenario walk: when the
           chosen date IS the earliest free arrival there is no spare day to
           ship early, so the sub-line is "Free shipping." alone
           (xd_comfortable_sub_same). Any later date keeps the full line. */
        sub = (v.earliestFree && chosen === v.earliestFree)
          ? copy('date-comfortable-sub-same') : copy('date-comfortable-sub');
        note = copy('date-comfortable-note');
      } else if (state === 'tight') {
        /* I1: the CHEAPEST paid service that makes the date, any carrier.
           Item 86 case 2: one sentence stating that service's commitment. */
        needService = v.service;
        verdictCtx = { data: data, target: chosen };
        var tl = tightLines(v);
        head = tl[0];
        sub = tl[1];
      } else if (state === 'past') {
        /* I1: past only when none of the seven makes it, and {earliest} is
           the earliest of the seven, from this same call (so it carries the
           blanket day when the call was flagged). The older Canada Post chain,
           order_by.arrival then this call's then the page's arrivals, stays as
           the fallback for an answer with no usable arrival at all. */
        var pob = (data && data.order_by) || {};
        var earliest = v.earliest || (parseIso(pob.arrival) ? pob.arrival : '') ||
          earliestArrival(arrivalsOf(data)) || earliestArrival(arrivals) || '';
        /* Item 86 case 3: the picker already blocks this date (min, this past
           state and blockAtc's need-reachable), so only the message changed:
           "Earliest available delivery is Tue, Sep 29. Choose Sep 29 or
           later." With no earliest date at all it says need-reachable. */
        var fillPast = function (t) {
          return withFree(t.split('{earliest_day}').join(dayShort(earliest))
            .split('{earliest}').join(shortNoYear(earliest))
            .split('{date}').join(shortNoYear(chosen)), v.earliestFree);
        };
        head = earliest ? fillPast(copy('date-past')) : copy('need-reachable');
        sub = earliest ? fillPast(copy('date-past-sub')) : '';
      } else {
        head = copy('date-estimate').replace('{date}', shortNoYear(chosen));
        sub = copy('date-estimate-sub');
      }

      setResult(head, sub, note);
      show(dateResult, true);
      syncInputs();
    }

    /* ---------- card 1: the postal check ---------- */
    function openPostal() {
      show(postalForm, true);
      show(openBtn, false);
      if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
    }
    function resetArrival() {
      show(arrival, false);
      show(asapBefore, true);
      belated = false;
      show(stickerRow, true);
    }
    /* Card 1 with its "Check estimated arrival" link closed and no arrival
       showing gets the link back, so a failure that started in card 2 never
       leaves card 1 with no way to open its own field. */
    function card1Idle() {
      show(openBtn, !!(postalForm && postalForm.hidden));
    }
    /* ESTIMATOR LOADING AND FAILURE STATE, card 1 only (2026-10-08). The
       See date button stacks its three labels in one grid cell, so the state
       class only picks which label shows and the width never moves. aria-busy
       marks the button and the postal form while a lookup is out. Null-safe. */
    function setSeeState(state) {
      if (seeBtn) {
        seeBtn.classList.remove('is-busy');
        seeBtn.classList.remove('is-retry');
        if (state === 'busy') seeBtn.classList.add('is-busy');
        if (state === 'retry') seeBtn.classList.add('is-retry');
      }
      [seeBtn, postalForm].forEach(function (el) {
        if (!el) return;
        if (state === 'busy') el.setAttribute('aria-busy', 'true'); else el.removeAttribute('aria-busy');
      });
    }
    /* The neutral "estimator unavailable" box under card 1's field. */
    function showDown(on) { if (downEl) downEl.hidden = !on; }
    /* Card 1's "See date" and Enter. `again` is a postal code already
       accepted, passed by a blanket change so a half-typed edit in the field
       is never submitted by a checkbox. As a click handler this receives an
       Event, which is not a string. */
    function check(again) {
      if (!postalEl) return;
      if (typeof again === 'string') { lookup(again, ''); return; }
      lookup(normPostal(postalEl.value), 'c1');
    }
    /* Card 2's "Confirm week" / "See dates" (was "Confirm date" until the
       2026-10-06 ruling) and Enter. */
    function confirm2() {
      if (!postal2El) return;
      lookup(normPostal(postal2El.value), 'c2');
    }
    /* THE ONE POSTAL LOOKUP. Card 1 (src 'c1'), card 2 (src 'c2') and the
       blanket re-check (src '') all come here with the active field's value.
       It writes the one `postal`; a success fills BOTH fields and paints card
       1's arrival line, which CSS shows only while card 1 is open. An engine
       rejection shows next to the field that asked. */
    function lookup(typed, src) {
      var field = src === 'c2' ? postal2El : postalEl;
      var btn = src === 'c2' ? confirm2Btn : seeBtn;
      if (!typed) { if (field && src) field.focus(); return; }
      var seq = ++checkSeq;
      if (src === 'c1') setErr(msgEl, '');
      if (src === 'c2') setErr(fieldErr, '');
      if (btn && src) btn.disabled = true;
      var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
      var busyTimer = src === 'c1' ? setTimeout(function () { setSeeState('busy'); if (postalEl) postalEl.readOnly = true; }, BUSY_DELAY_MS) : null;
      var watchdog = setTimeout(function () { if (ctrl) ctrl.abort(); }, LOOKUP_MS);
      var url = flagged(endpoint + '?postal=' + encodeURIComponent(typed));
      /* Verification hook: sessionStorage mdp_eta_sim = 'down' answers null at
         once, 'slow' never answers (the watchdog ends it). Unset is the real call. */
      var sim = ''; try { sim = window.sessionStorage.getItem('mdp_eta_sim') || ''; } catch (err) {}
      var req = sim === 'down' ? Promise.resolve(null) : sim === 'slow' ? new Promise(function () {}) : getJson(url, ctrl ? ctrl.signal : undefined);
      Promise.race([req, new Promise(function (res) { setTimeout(function () { res(null); }, LOOKUP_MS + 50); })]).then(function (data) {
        clearTimeout(busyTimer); clearTimeout(watchdog);
        if (src === 'c1') { setSeeState('idle'); if (postalEl) postalEl.readOnly = false; }
        /* A failed or abandoned answer is not kept, so Try again really asks. */
        if (!data) { delete cache[url]; }
        if (seq !== checkSeq) return;
        if (btn && src) btn.disabled = false;

        if (data && data.status === 'invalid_postal') {
          postal = '';
          showDown(false);
          arrivals = Object.create(null);
          fedexArrivals = Object.create(null);
          resetArrival();
          card1Idle();
          if (src === 'c1') setErr(msgEl, 'invalid');
          if (src === 'c2') setErr(fieldErr, 'invalid');
          if (mode === 'week') applyRanges(baseRanges); else { applyMinDate(); syncInputs(); }
          if (field && src) field.focus();
          return;
        }
        if (!data || data.status !== 'ok') {
          /* C1(a), widened by Tommy 2026-09-22: no data at all (network
             error, non-2xx) OR any non-ok answer other than invalid_postal,
             `unavailable` included, means the engine cannot help. Fail open
             (C4, C5). Nothing was rejected, so a code already accepted stays,
             and card 1 is left as it is. */
          lookupDown = true;
          if (src === 'c1') { showDown(true); setSeeState('retry'); if (postalEl) postalEl.readOnly = false; }
          goDown();
          return;
        }
        postal = typed;
        showDown(false);
        /* A lookup that failed earlier has now succeeded: the engine is back,
           so card 2 resumes asking for the code and giving verdicts. The
           success path below then makes its ordinary calls. */
        if (lookupDown) { lookupDown = false; engineDown = false; }
        arrivals = arrivalsOf(data);
        fedexArrivals = fedexArrivalsOf(data);
        storeSet(typed);
        postalEl.value = displayPostal(typed);
        /* A blanket re-check never overwrites an edit open in card 2. */
        if (src || !postalEdit) {
          postalEdit = false;
          if (postal2El) postal2El.value = displayPostal(typed);
        }
        setErr(msgEl, '');
        setErr(fieldErr, '');

        /* ITEM 86, ruled by Tommy 2026-09-27 from the scenario walk: card 1's
           headline is the EARLIEST FREE arrival across BOTH free services,
           Expedited Parcel and FedEx Ground, the same pair and the same date
           the free card picks (summaryCards), so the headline never reads
           later than the block's own free card. It used to read Expedited
           Parcel alone. The free card and the compare list are unchanged. */
        var fg = fedexArrivals['fedex_ground'];
        var ep = arrivals['DOM.EP'] || '';
        if (fg && fg.arrival && (!ep || fg.arrival < ep)) ep = fg.arrival;
        if (ep) {
          belated = mode === 'week' && !!(parseIso(xmas) && ep > xmas);
          var sub = copy(belated ? 'belated-sub' : 'asap-sub').replace('{postal}', displayPostal(typed));
          if (arrivalHead) arrivalHead.textContent = copy('arrives') + ' ' + weekdayLong(ep);
          if (arrivalSub) arrivalSub.textContent = sub + ' ';
          show(asapBefore, false);
          show(arrival, true);
          show(postalForm, false);
          show(openBtn, false);
          show(stickerRow, !belated);
        } else {
          /* No Expedited Parcel option: the before-postal line stays. */
          resetArrival();
          card1Idle();
        }

        if (mode === 'week') {
          if (belated) { applyRanges([]); return; }
          syncInputs();
          rangesForPostal(typed).then(function (list) {
            if (seq !== checkSeq) return;
            applyRanges(list);
          });
          return;
        }

        applyMinDate();
        if (chosen) onDatePicked(); else syncInputs();
      });
    }

    /* ---------- events ---------- */
    if (openBtn) openBtn.addEventListener('click', function () { openPostal(); if (postalEl) postalEl.focus(); });
    if (seeBtn) seeBtn.addEventListener('click', check);
    if (postalEl) postalEl.addEventListener('keydown', function (e) {
      /* Enter inside the product form would submit it; run the check instead.
         Ruled by Tommy 2026-10-06: Enter does exactly what the button does and
         never reaches the form. preventDefault cancels the implicit submit;
         stopPropagation keeps the keydown from any form-level or document
         listener as well, so mouse and keyboard end in the same state. */
      if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); check(); }
    });
    /* CHANGE FORGETS THE OLD CODE (ruled by Tommy 2026-09-23, from his walk).
       Card 1's Change and card 2's Change, both modes. Until then Change only
       reopened the field, so the old code kept driving the verdict, the
       prices and Shipping needed, and came back from sessionStorage on the
       next page. Now: retire in-flight calls, drop the code and every answer
       built on it, remove the stored key, put card 1 back to its idle line,
       drop the calendar floor to today + 2, hide the date result, and in week
       mode drop the per-postal states while KEEPING the chosen week. The
       chosen date is KEPT too (ruled 2026-09-21), so confirming the new code
       re-judges it through the ordinary lookup path. The field the shopper
       pressed Change on opens EMPTY with focus. lookup() is unchanged. */
    function forgetPostal(src) {
      ++checkSeq;
      ++targetSeq;
      postal = '';
      arrivals = Object.create(null);
      fedexArrivals = Object.create(null);
      chosenState = '';
      needService = '';
      verdictCtx = null;
      verdictNow = null;
      try { window.sessionStorage.removeItem(STORE_KEY); } catch (e) {}
      if (postalEl) postalEl.value = '';
      if (postal2El) postal2El.value = '';
      setErr(msgEl, '');
      setErr(fieldErr, '');
      resetArrival();
      card1Idle();
      applyMinDate();
      show(dateResult, false);
      if (src === 'c1') openPostal(); else postalEdit = true;
      /* applyRanges() re-runs weekVerdict() (which returns at once with no
         postal code) and syncInputs(); syncInputs() hides the price lists
         while the code is blank and, near a date or week, shows card 2's own
         postal field as the step. */
      /* Date mode, ruled 2026-09-23: a chosen date is re-judged at once
         through onDatePicked(), so a far date re-shows the estimate pair and
         a near date makes card 2's own postal field the step (Rule D). Only
         while card 2 is selected: onDatePicked() selects card 2 itself, and
         card 1's Change is pressed with card 1 open, so a date left over from
         an earlier visit to card 2 must not pull the shopper across. */
      if (mode === 'week') applyRanges(baseRanges);
      else if (chosen && altMode()) onDatePicked();
      else syncInputs();
      var f = src === 'c1' ? postalEl : postal2El;
      if (f) f.focus();
    }
    if (changeBtn) changeBtn.addEventListener('click', function () { forgetPostal('c1'); });
    /* card 2's own field and the two "Change" links */
    if (confirm2Btn) confirm2Btn.addEventListener('click', confirm2);
    if (postal2El) postal2El.addEventListener('keydown', function (e) {
      /* Same ruling, 2026-10-06: Enter is the "See dates" / "Confirm week"
         button, and never the product form. */
      if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); confirm2(); }
    });
    if (postalChange) postalChange.addEventListener('click', function () { forgetPostal('c2'); });
    if (weekChange) weekChange.addEventListener('click', function () {
      /* The buttons come back with the chosen week still pressed. */
      weekOpen = true;
      syncInputs();
      var pressed = weeksEl && weeksEl.querySelector('[aria-pressed="true"]');
      if (pressed) pressed.focus();
    });
    if (stickerCheck) stickerCheck.addEventListener('change', syncInputs);
    if (dateEl && mode !== 'week') {
      dateEl.addEventListener('change', onDatePicked);
      dateEl.addEventListener('input', onDatePicked);
    }
    /* The blanket day. Giftship re-renders its container, so the listener sits
       on the document, never on the checkbox, and the value is re-read when
       Giftship says it has (re)loaded. Nothing happens unless it CHANGES. */
    function onBlanket() {
      var now = blanketTicked();
      if (now === personalized) return;
      personalized = now;
      if (mode === 'birthday') return;
      /* A target answer still in flight was asked with the old flag. Retire it
         now so it can never paint over the fresh one, in either direction;
         check() retires an in-flight postal call the same way through
         checkSeq, and re-runs onDatePicked() itself when a date is chosen.
         Until the fresh answer lands, the verdict on screen is the previous
         one, exactly as after any other re-check. */
      ++targetSeq;
      if (postal) check(postal);
      /* No accepted postal code: there is no engine answer on the page to
         refresh, and the next call is flagged on its own. */
    }
    document.addEventListener('change', onBlanket, true);
    document.addEventListener('giftship.loaded', onBlanket);
    document.addEventListener('giftship.reloaded', onBlanket);

    [radioAsap, radioAlt].forEach(function (r) {
      if (r) r.addEventListener('change', function () {
        /* Any radio change clears every error, in both cards. */
        clearErrors();
        syncInputs();
      });
    });

    /* ---------- add-to-cart guard ----------
       Birthday installs none: a blank date is always allowed (F4).
       Card 2 only. A postal code is needed only where rules C and D say so.
       need-week, need-date and need-reachable are card-level errors at the
       top of card 2; need-postal is a field-level error under card 2's own
       field. The scroll target is the error; need-postal focuses the field.
       RULED 2026-09-22: a blank date on card 2 now blocks with need-date,
       which SUPERSEDES F4's "a blank date on card 2 stays ASAP" in date mode.
       Birthday keeps F4. */
    function blockAtc(e) {
      if (mode === 'birthday') return false;
      if (!altMode()) return false;
      var need = '';
      /* While the probe (C2) is still out the postal step is not on screen,
         and Add to Cart is ALLOWED (ruled by Tommy 2026-09-22), never held. */
      var needPostal = postalRequired() && !postal && probeState !== 'pending';
      if (mode === 'week') need = !week ? 'need-week' : (needPostal ? 'need-postal' : '');
      /* Item 76: need-postal is named BEFORE need-date in date mode, because
         the date field is disabled while the postal step is on screen and
         "pick a delivery date" would point at a field the shopper cannot
         use. It read `!chosen ? 'need-date' : (needPostal ? 'need-postal'
         : ...)` before. Both still block; only the order of the message
         changed. */
      else need = needPostal ? 'need-postal' : (!chosen ? 'need-date' : (chosenState === 'past' ? 'need-reachable' : ''));
      if (!need) return false;
      e.preventDefault();
      e.stopImmediatePropagation();
      e.stopPropagation();
      var errEl;
      if (need === 'need-postal') {
        weekOpen = false;
        syncInputs();              /* card 2's postal field is now the visible step */
        setErr(fieldErr, need);
        errEl = fieldErr;
      } else {
        setErr(cardErr, need);
        errEl = cardErr;
      }
      try { if (errEl) errEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (err) {}
      if (need === 'need-postal' && postal2El) {
        try { postal2El.focus({ preventScroll: true }); } catch (err) { postal2El.focus(); }
      }
      return true;
    }
    if (form && mode !== 'birthday') {
      window.addEventListener('click', function (e) {
        var atc = e.target && e.target.closest && e.target.closest('[data-mdp-atc]');
        if (!atc || atc.form !== form && atc.closest('form') !== form) return;
        blockAtc(e);
      }, true);
      window.addEventListener('submit', function (e) {
        if (e.target !== form) return;
        blockAtc(e);
      }, true);
    }

    /* ---------- boot ---------- */
    /* A browser restoring a form on reload can hand us a date field that is
       already filled, so read it before the first sync rather than after. */
    if (dateEl && mode !== 'week') chosen = dateEl.value || '';
    syncInputs();

    if (mode === 'birthday') return;

    applyMinDate();
    var saved = storeGet();
    function prefill() {
      if (saved && postalEl && !postalEl.value) {
        postalEl.value = displayPostal(saved);
        openPostal();
        check();
      }
    }
    if (mode === 'week') {
      loadBaseRanges().then(function (list) {
        baseRanges = list;
        applyRanges(list);
        prefill();
      });
    } else {
      prefill();
    }
  }

  function boot() {
    var roots = document.querySelectorAll('[data-st-root]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
